import { useState, useEffect } from 'react';
import { useDebounceCallback } from '@react-hook/debounce';
import _ from 'lodash';
import {
  isInactiveRepository,
  getRepositoriesFromProvider,
  fetchOrgs,
  getCursor,
  isValidTerm,
  formatSelectedReposForSave,
  getActiveRepositories,
  destructureSelected,
  getTotalSelectedRepositories,
  handleSelection,
  NO_GIT_ORGS_ERROR,
  getOrgValueFromNewRepos
} from './repositories-modal.service';
import {
  getNumOfSelectableRepos,
  isInOnboardingPath,
  capitalizeDropdownDisplayValue,
  isTeamLicense
} from 'common/utils/utils';
import * as TrackingService from 'common/services/tracking.service';
import {
  PROVIDER_NAMES,
  SESSION_STORAGE_KEYS,
  TRACKING
} from 'common/constants/constants';
import { MAX_REPO_NUM_FOR_FREE_DORA_DURING_ONBOARDING } from './repositories.modal.consts';
import { isProviderWithOrgs } from 'common/utils/git-providers.utils';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';

// eslint-disable-next-line max-lines-per-function
const useRepositoriesModal = ({
  sensor_authorization_id,
  repositories,
  license,
  createRepositories,
  onSubmitCb,
  onRemoveMessage,
  newRepositories,
  providerConfig,
  sensor_name,
  discoveredRepositories,
  isGithubFetchAllRepoTypesFF,
  searchCharsLimit,
  search,
  account,
  isPat,
  isConnectGitStreamFlow
}) => {
  const githubFilterByRepoType = isGithubFetchAllRepoTypesFF
    ? { visibility: 'all' }
    : {};
  const rowsPerPage = 100;
  const [total, setTotal] = useState(0);
  const [pageParams, setPageParams] = useState({});
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [numOfSelectableRepos, setNumOfSelectableRepos] = useState(5);
  const [numOfFreeSlots, setNumOfFreeSlots] = useState(0);
  const [selected, setSelected] = useState({});
  const [term, setTerm] = useState(search);
  const [error, setError] = useState(null);
  const isOnboarding = isInOnboardingPath();
  const [orgRepositories, setOrgRepositories] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [isSinglePage, setIsSinglePage] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [unselectedForNewRepos, setUnselectedForNewRepos] = useState(new Set());
  const selectedByOrg = _.get(selected, `[${selectedOrg}]`, {});
  const { totalReposByOrg, inactiveRepos, isSelectAll } =
    destructureSelected(selectedByOrg);

  const hasNoActiveRepos = orgRepositories?.every(
    ({ isInactive }) => isInactive
  );

  const isBitbucket = sensor_name?.toLowerCase() === PROVIDER_NAMES.bitbucket;
  const fetchOrgsToUi = async (orderByField = null, orderAsc = true) => {
    const orgs = await fetchOrgs(
      sensor_authorization_id,
      orderByField,
      orderAsc
    );
    return orgs.map(org => ({
      value: org.name,
      displayValue: org.name
    }));
  };

  const fetchRepositories = useDebounceCallback(async () => {
    setLoading(true);
    const paramCursor = getCursor(pageParams, page, isBitbucket);

    if (!isValidTerm(term, searchCharsLimit)) {
      setLoading(false);
      return false;
    }
    const response = await getRepositoriesFromProvider({
      sensor_authorization_id,
      repositories,
      isOnboarding,
      providerConfig,
      selectedOrg,
      rowsPerPage,
      paramCursor,
      q: term,
      githubFilterByRepoType,
      isBitbucket
    });

    const { cursor, items, total, error } = response;

    if (error) {
      setError(error);
      return false;
    }

    const newRepositoriesHttpUrl = newRepositories.map(
      ({ http_url }) => http_url
    );

    const selectableRepositoriesWithStatus = items.map(repo => {
      const isInactive = isInactiveRepository(repo.updated_at);
      const isDiscovered = isOnboarding
        ? false
        : !_.isEmpty(
            discoveredRepositories.find(
              discoveredRepo =>
                discoveredRepo.provider_id === repo.id ||
                discoveredRepo.http_url === repo.http_url
            )
          );
      const isNew = newRepositoriesHttpUrl.includes(repo.http_url);
      return {
        ...repo,
        isInactive,
        isDiscovered,
        isNew
      };
    });

    const orderedRepositories = _.chain(selectableRepositoriesWithStatus)
      .orderBy(['isNew', 'isDiscovered', 'updated_at'], ['desc', 'asc', 'desc'])
      .value();

    setOrgRepositories(orderedRepositories);

    const activeRepositories = getActiveRepositories(orderedRepositories) || [];
    const inactiveRepositories =
      _.difference(orderedRepositories, activeRepositories) || [];

    if (total) {
      setTotal(total);
    }

    if (!totalReposByOrg && rowsPerPage > total && orgs.length === 1) {
      setIsSinglePage(true);
    }

    const newRepositoriesIds = orderedRepositories
      .filter(({ isNew, id }) => isNew && !unselectedForNewRepos.has(id))
      .map(({ id }) => id);
    const selectedNewRepositories = handleSelection({
      repositoriesIds: newRepositoriesIds,
      selectedByOrg,
      action: 'selectAllInPage',
      cursor: getCursor(pageParams, page, isBitbucket),
      unselectedForNewRepos,
      setUnselectedForNewRepos
    });

    setSelected({
      ...selected,
      [selectedOrg]: {
        ...selected[selectedOrg],
        inactiveRepos: new Set([
          ...inactiveRepositories.map(({ id }) => id),
          ...inactiveRepos
        ]),
        ...(!isSelectAll ? { ...selectedNewRepositories } : {}),
        ...(activeRepositories.length ? { isFoundActiveRepos: true } : {}),
        ...(!totalReposByOrg && total ? { total } : {})
      }
    });

    setPageParams({
      ...pageParams,
      [page]: {
        next: cursor
      }
    });
    setLoading(false);
  }, 700);

  useEffect(() => {
    if (isOnboarding && error) {
      TrackingService.logEvent(TRACKING.EVENTS.SETUP_WIZARD_SCAN_REPOS_FAIL, {
        identity_email: account?.email,
        git_provider: sensor_name,
        auth: isPat ? 'pat' : 'oauth',
        reason: error
      });
    }
  }, [error, isOnboarding]);
  useEffect(() => {
    let numOfSelectableRepos = getNumOfSelectableRepos({
      license
    });

    // For onboarding case we want to limit the selectable repos to 5
    if (isOnboarding && isTeamLicense()) {
      numOfSelectableRepos = MAX_REPO_NUM_FOR_FREE_DORA_DURING_ONBOARDING;
    }

    const activate = async () => {
      const shouldFetchRepositories = numOfSelectableRepos > 0;
      setNumOfSelectableRepos(numOfSelectableRepos);
      setLoading(shouldFetchRepositories);

      if (shouldFetchRepositories) {
        if (isProviderWithOrgs(sensor_name?.toLowerCase())) {
          const sortingField =
            sensor_name?.toLowerCase() === PROVIDER_NAMES.github
              ? 'total_private_repos'
              : 'name';
          const orderAsc = sensor_name?.toLowerCase() !== PROVIDER_NAMES.github;

          let orgs = [];

          try {
            orgs = (await fetchOrgsToUi(sortingField, orderAsc)) || [];
          } catch (err) {
            const responseCode = _.get(err, 'response.status', 500);
            setError(responseCode);
            return false;
          }

          if (orgs.length) {
            const selectedOrgValue = newRepositories.length
              ? getOrgValueFromNewRepos({
                  newRepositories,
                  orgs
                })
              : orgs[0].value;
            setOrgs(capitalizeDropdownDisplayValue(orgs));
            setSelectedOrg(selectedOrgValue);
          } else {
            setError(NO_GIT_ORGS_ERROR);
            setLoading(false);
            return false;
          }
        } else {
          setSelectedOrg(sensor_name);
        }
        await fetchRepositories();
      } else {
        TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
          page: isOnboarding
            ? TRACKING.PAGES.ONBOARDING
            : TRACKING.PAGES.SETTINGS,
          page_tab: TRACKING.PAGE_TABS.SELECT_REPOS
        });
      }
    };
    activate();
  }, []);

  useEffect(() => {
    if (isValidTerm(term, searchCharsLimit) && selectedOrg !== '') {
      fetchRepositories();
    }
  }, [term, page, selectedOrg]);

  useEffect(() => {
    const numOfFreeSlots =
      numOfSelectableRepos - getTotalSelectedRepositories(selected);
    setNumOfFreeSlots(numOfFreeSlots);
  }, [selected]);

  const onSubmit = async () => {
    const available_repositories = discoveredRepositories;
    const selectedReposFilter = formatSelectedReposForSave({ selected });

    if (isConnectGitStreamFlow) {
      BrowserStorageAccessor().session.set(
        SESSION_STORAGE_KEYS.UPDATE_GITSTREAM_ON_INTEGRATION_ID,
        sensor_authorization_id
      );
    }

    setSaving(true);
    const message = await createRepositories(
      {
        sensor_authorization_id,
        repositories_filters: selectedReposFilter,
        origin: isOnboarding ? 'onboarding' : 'add_repositories',
        ...githubFilterByRepoType
      },
      null,
      true
    );

    await onSubmitCb({
      available_repositories,
      messageId: _.get(message, 'MessageId', 0),
      isV2: true
    });
    if (isOnboarding) {
      TrackingService.logEvent(TRACKING.EVENTS.SETUP_WIZARD_SCAN_REPOS_OK, {
        identity_email: account?.email,
        git_provider: sensor_name,
        auth: isPat ? 'pat' : 'oauth',
        repos: totalReposByOrg
      });
    }
    await onRemoveMessage();
    setSaving(false);
  };

  return {
    onSubmit,
    setTerm,
    setLoading,
    setSelected,
    orgRepositories,
    loading,
    saving,
    error,
    numOfSelectableRepos,
    numOfFreeSlots,
    selected,
    term,
    isOnboarding,
    orgs,
    selectedOrg,
    setSelectedOrg,
    rowsPerPage,
    page,
    setPage,
    total,
    pageParams,
    setPageParams,
    isSinglePage,
    unselectedForNewRepos,
    setUnselectedForNewRepos,
    isBitbucket,
    hasNoActiveRepos,
    onRefetch: () => fetchRepositories()
  };
};

export default useRepositoriesModal;
